import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Newspaper, Search, ArrowRight, Lightbulb } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { SEOHead, breadcrumbSchema, organizationSchema } from '../components/SEOHead';
import { BlogCard } from '../components/BlogCard';
import { swallowWithLog } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

export default function BlogPage() {
  const [params, setParams] = useSearchParams();
  const category = params.get('categorie') || '';
  const [query, setQuery] = useState(params.get('q') || '');
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/blog/categories`).then((r) => setCategories(r.data.categories || [])).catch(swallowWithLog('blog categories'));
  }, []);

  const load = useCallback(async (p, append = false) => {
    setLoading(true); setError(false);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/blog`, { params: { category: category || undefined, q: params.get('q') || undefined, page: p, limit: 9 } });
      setItems((prev) => (append ? [...prev, ...res.data.items] : res.data.items));
      setTotal(res.data.total); setPages(res.data.pages); setPage(p);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [category, params]);

  useEffect(() => { load(1); }, [load]);

  const setCategory = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set('categorie', id); else next.delete('categorie');
    setParams(next);
  };
  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (query.trim()) next.set('q', query.trim()); else next.delete('q');
    setParams(next);
  };

  const activeCat = categories.find((c) => c.id === category);
  const title = activeCat ? `Blog ${activeCat.label}` : 'Blog — Conseils maison, jardin et crédit d\'impôt';
  const description = activeCat
    ? `Articles de fond sur ${activeCat.label.toLowerCase()} par Ptitservices, votre facilitateur de vies dans l'Artois.`
    : 'Le blog Ptitservices : guides complets sur le ménage, le jardinage, le crédit d\'impôt 50 % et la vie pratique à domicile dans l\'Artois.';

  const hasFilter = Boolean(params.get('q') || category);

  const renderArticles = () => {
    if (loading && items.length === 0) {
      return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-loading">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={`blog-skel-${i}`} className="h-80 rounded-2xl" />)}</div>
      );
    }
    if (error) {
      return (
        <div className="text-center py-16" data-testid="blog-error">
          <p className="text-muted-foreground mb-4">Impossible de charger les articles pour le moment.</p>
          <Button variant="outline" onClick={() => load(1)}>Réessayer</Button>
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div className="text-center py-16 max-w-md mx-auto" data-testid="blog-empty">
          <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--brand-mint))] flex items-center justify-center mx-auto mb-4"><Newspaper className="h-7 w-7 text-[hsl(var(--primary))]" /></div>
          <h2 className="font-['Space_Grotesk'] text-xl font-semibold mb-2">{hasFilter ? 'Aucun article ne correspond' : 'Les premiers articles arrivent bientôt'}</h2>
          <p className="text-muted-foreground text-sm mb-6">{hasFilter ? 'Essayez une autre catégorie ou un autre mot-clé.' : 'En attendant, découvrez nos astuces et tutoriels quotidiens.'}</p>
          {hasFilter ? (
            <Button variant="outline" onClick={() => { setQuery(''); setParams(new URLSearchParams()); }}>Voir tous les articles</Button>
          ) : (
            <Link to="/ptitconseils"><Button><Lightbulb className="mr-2 h-4 w-4" /> Découvrir les Ptitconseils</Button></Link>
          )}
        </div>
      );
    }
    return (
      <>
        <p className="text-sm text-muted-foreground mb-6" data-testid="blog-count">{total} article{total > 1 ? 's' : ''}{activeCat ? ` · ${activeCat.label}` : ''}{params.get('q') ? ` · « ${params.get('q')} »` : ''}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
          {items.map((post) => <BlogCard key={post.id} post={post} />)}
        </div>
        {page < pages && (
          <div className="text-center mt-10">
            <Button variant="outline" onClick={() => load(page + 1, true)} disabled={loading} data-testid="blog-load-more">{loading ? 'Chargement…' : 'Voir plus d\'articles'}</Button>
          </div>
        )}
      </>
    );
  };

  return (
    <div data-testid="blog-page">
      <SEOHead title={title} description={description} path={category ? `/blog?categorie=${category}` : '/blog'}
        schema={[breadcrumbSchema([{ name: 'Accueil', path: '/' }, { name: 'Blog', path: '/blog' }]),
          { '@context': 'https://schema.org', '@type': 'Blog', 'name': 'Blog Ptitservices', 'url': 'https://ptitservices.com/blog', 'description': description, 'publisher': organizationSchema(), 'inLanguage': 'fr-FR' }]} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} className="max-w-2xl">
            <Badge className="mb-4 px-4 py-1.5 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"><Newspaper className="h-3.5 w-3.5 mr-1.5" /> Le blog</Badge>
            <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4">Des articles de fond pour une maison <span className="text-[hsl(var(--primary))]">plus simple à vivre</span></h1>
            <p className="text-muted-foreground text-lg leading-relaxed">Guides pratiques, décryptage du crédit d'impôt et coulisses des Easers : tout ce qu'il faut savoir pour vous faciliter la vie, dans l'Artois et ailleurs.</p>
          </motion.div>

          <div className="mt-8 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Catégories" data-testid="blog-categories">
              <Button size="sm" variant={!category ? 'default' : 'outline'} className="rounded-full" onClick={() => setCategory('')} data-testid="blog-cat-all">Tous</Button>
              {categories.map((c) => (
                <Button key={c.id} size="sm" variant={category === c.id ? 'default' : 'outline'} className="rounded-full" onClick={() => setCategory(c.id)} data-testid={`blog-cat-${c.id}`}>
                  {c.label}{c.count > 0 && <span className="ml-1.5 text-[11px] opacity-70">{c.count}</span>}
                </Button>
              ))}
            </div>
            <form onSubmit={submitSearch} className="relative lg:ml-auto w-full lg:w-72" role="search">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher un article…" className="pl-9 bg-card" aria-label="Rechercher un article" data-testid="blog-search-input" />
            </form>
          </div>
        </div>
      </section>

      {/* List */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {renderArticles()}
        </div>
      </section>

      {/* Cross-link to Ptitconseils */}
      <section className="py-12 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-['Space_Grotesk'] text-xl font-semibold mb-1">Envie d'astuces express ?</h2>
            <p className="text-sm text-muted-foreground">Chaque jour, un Ptitconseil : une astuce ou un tutoriel illustré pour la maison et le jardin.</p>
          </div>
          <Link to="/ptitconseils"><Button variant="outline">Voir les Ptitconseils <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
        </div>
      </section>
    </div>
  );
}
